<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import IconCheckCircle from '~/assets/icons/IconCheckCircle.vue';
import IconFileText from '~/assets/icons/IconFileText.vue';
import StatusCard from '~/components/shared/StatusCard.vue';
import Tabs, { type Tab } from '~/components/shared/Tabs.vue';
import { FinancingFlowApi } from '~/services/financing-flow-api/financing-flow.types';

import useCustomerRegistrationDetails from '../../queries/useCustomerRegistrationDetails';
import BillingContent from '../billing/BillingContent.vue';
import AddressDataContent from '../CustomerData/AddressDataContent.vue';
import CustomerDataContent from '../CustomerData/CustomerDataContent.vue';
import OtherUnitsContent from '../CustomerData/OtherUnitsContent.vue';
import ProjectDataContent from '../CustomerData/ProjectDataContent.vue';

type CustomerDataTabId = 'address' | 'customer' | 'other-units' | 'payment' | 'project';

interface Props {
  financing: FinancingFlowApi.FinancingV2;
}

const props = defineProps<Props>();

const completedAt = computed(
  () => props.financing.section_statuses.customer_data?.completed_at || undefined,
);
const submittedAt = computed(
  () => props.financing.section_statuses.customer_data?.submitted_at || undefined,
);

const projectId = computed(() => props.financing.project_id);
const registrationQuery = useCustomerRegistrationDetails(projectId);
const isLoading = computed(() => registrationQuery.isLoading.value || registrationQuery.isFetching.value);
const isError = computed(() => registrationQuery.isError.value);

const activeTab = ref<CustomerDataTabId>('customer');

const tabs = computed<Tab<CustomerDataTabId>[]>(() => [
  { id: 'customer', label: 'Dados do cliente' },
  { id: 'address', label: 'Endereço do cliente' },
  { id: 'project', label: 'Dados do projeto' },
  { id: 'other-units', label: 'Outras unidades' },
  { id: 'payment', label: 'Dados de pagamento' },
]);

watch(
  tabs,
  (newTabs) => {
    const tabIds = newTabs.map((tab) => tab.id);
    if (!tabIds.includes(activeTab.value)) {
      activeTab.value = 'customer';
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="customer-data-step">
    <div class="mb-4">
      <div class="row g-3">
        <div class="status-card-col">
          <StatusCard
            title="Submetido em"
            :datetime="submittedAt"
            :icon="IconFileText"
            type="informative"
          />
        </div>

        <div class="status-card-col">
          <StatusCard
            title="Concluído em"
            :datetime="completedAt"
            :icon="IconCheckCircle"
            type="positive"
          />
        </div>
      </div>
    </div>

    <div class="mt-4">
      <div v-if="isError" class="error-message">
        <div class="alert alert-danger">
          <div class="alert-title">Erro ao carregar dados</div>
          <p>Não foi possível carregar os dados do cliente. Tente novamente mais tarde.</p>
        </div>
      </div>

      <Tabs v-else v-model="activeTab" :tabs="tabs">
        <template #default="{ activeTab: currentTab }">
          <div v-if="currentTab === 'customer'" class="mt-4">
            <CustomerDataContent :financing="financing" :is-loading="isLoading" />
          </div>

          <div v-else-if="currentTab === 'address'" class="mt-4">
            <AddressDataContent
              :person-type="financing.person_type"
              :project-id="projectId"
              :is-loading="isLoading"
            />
          </div>

          <div v-else-if="currentTab === 'project'" class="mt-4">
            <ProjectDataContent :project-id="projectId" :is-loading="isLoading" />
          </div>

          <div v-else-if="currentTab === 'other-units'" class="mt-4">
            <OtherUnitsContent :project-id="projectId" :is-loading="isLoading" />
          </div>

          <div v-else-if="currentTab === 'payment'" class="mt-4">
            <BillingContent :financing="financing" />
          </div>
        </template>
      </Tabs>
    </div>
  </div>
</template>

<style scoped lang="scss">
.customer-data-step {
  .status-card-col {
    @media (min-width: 768px) {
      flex: 0 0 auto;
      width: 50%;
    }
  }

}
</style>
